import React, { useEffect, useMemo } from 'react';
import { Select, Spin, message, Tooltip } from 'antd';
import { SwapOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { ApplicationState } from '../../reducers';
import { fetchAccessibleAccounts, switchAccount, loadUserByToken } from '../../actions/user';
import { useAppDispatch } from '../../store/ReduxStore';

const { Option } = Select;

type AccountSelectorProps = {
  collapsed?: boolean;
};

const AccountSelector: React.FC<AccountSelectorProps> = ({ collapsed = false }) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const { accessibleAccounts, loading, switching, user } = useSelector(
    (state: ApplicationState) => ({
      accessibleAccounts: state.user.accessibleAccounts,
      loading: state.user.loadingAccessibleAccounts,
      switching: state.user.switchingAccount,
      user: state.user.user,
    })
  );

  useEffect(() => {
    if (!accessibleAccounts && user) {
      dispatch(fetchAccessibleAccounts());
    }
  }, [dispatch, accessibleAccounts, user]);

  const currentAccountId = useMemo(() => {
    return accessibleAccounts?.current_account_id || user?.account_id || null;
  }, [accessibleAccounts, user]);

  const accounts = useMemo(() => {
    return accessibleAccounts?.accessible_accounts || [];
  }, [accessibleAccounts]);

  const currentAccount = useMemo(
    () => accounts.find(acc => acc.id === currentAccountId),
    [accounts, currentAccountId]
  );
  const hasMultipleAccounts = accounts.length > 1;
  const showSearch = accounts.length > 10;

  const handleAccountChange = async (accountId: number) => {
    if (accountId === currentAccountId) return;

    try {
      // Switch to the target account
      await dispatch(switchAccount(accountId, t('accountSelector:switchReasonNavigation')));

      // Update accessible accounts list first so UI updates immediately
      await dispatch(fetchAccessibleAccounts());

      // Reset Redux and load user by token (like fresh login)
      // This resets all state and triggers app re-initialization naturally
      await (dispatch(loadUserByToken()) as unknown as Promise<any>);

      message.success(t('accountSelector:switchSuccess'));
    } catch (error: any) {
      message.error(error.response?.data?.message || t('accountSelector:switchError'));
    }
  };

  const getAccountTooltipContent = (account: {
    id: number;
    parent_id?: number;
    receiver_id?: number;
  }) => {
    const parts = [`ID: ${account.id}`];
    if (account.parent_id) {
      parts.push(`Parent ID: ${account.parent_id}`);
    }
    if (account.receiver_id) {
      parts.push(`Receiver ID: ${account.receiver_id}`);
    }
    return parts.join(' • ');
  };

  if (collapsed || !hasMultipleAccounts) {
    return null;
  }

  if (loading) {
    return (
      <div className="sidebar__account-selector-wrapper">
        <div className="sidebar__account-selector-loading">
          <Spin size="small" />
        </div>
      </div>
    );
  }

  const renderAccountLabel = (account: typeof currentAccount) => {
    if (!account) return null;
    return (
      <div className="sidebar__account-label-with-info">
        <span>{account.name}</span>
        <Tooltip title={getAccountTooltipContent(account)} placement="right">
          <InfoCircleOutlined className="sidebar__account-info-icon" />
        </Tooltip>
      </div>
    );
  };

  const filterOption = (input: string, option: any) => {
    const account = accounts.find(acc => acc.id === option.value);
    if (!account) return false;
    const searchText = input.toLowerCase();
    return (
      account.name.toLowerCase().includes(searchText) || account.id.toString().includes(searchText)
    );
  };

  return (
    <div className="sidebar__account-selector-wrapper">
      <Select
        value={currentAccountId}
        onChange={handleAccountChange}
        className="sidebar__account-selector"
        popupClassName="sidebar__account-selector-dropdown"
        suffixIcon={<SwapOutlined />}
        loading={switching}
        disabled={switching}
        dropdownMatchSelectWidth={false}
        placeholder={t('accountSelector:selectAccount')}
        showSearch={showSearch}
        filterOption={showSearch ? filterOption : undefined}
        optionFilterProp="children"
        labelRender={({ value }) => {
          const account = accounts.find(acc => acc.id === value);
          return renderAccountLabel(account);
        }}
      >
        {accounts.map(account => (
          <Option key={account.id} value={account.id}>
            <Tooltip title={getAccountTooltipContent(account)} placement="right">
              <div className="sidebar__account-option">
                <div className="sidebar__account-option-header">
                  <div className="sidebar__account-name">{account.name}</div>
                  {account.is_admin && (
                    <span className="sidebar__account-admin-badge">
                      {t('accountSelector:admin')}
                    </span>
                  )}
                </div>
              </div>
            </Tooltip>
          </Option>
        ))}
      </Select>
    </div>
  );
};

export default AccountSelector;
